/**
 * Integrations Page
 *
 * User-facing page for managing third-party app connections via Composio.
 * Protected route — requires authentication.
 *
 * /integrations/callback doubles as the OAuth landing. When the OAuth window
 * was a POPUP (onboarding / in-chat connect card — detected via the
 * localStorage handshake in ComposioClient), the callback renders a minimal
 * "connected" confirmation instead of this whole page: the popup used to
 * mount the full panel and auto-open a folder picker there while the opener
 * showed its own scope ask — the owner's "it asked me which folders twice"
 * report (2026-08-12).
 */

import React, { useEffect, useState } from 'react';
import { IntegrationsPanel } from '../services/composio';
import {
  clearConnectPopupLaunch,
  readConnectPopupLaunch,
} from '../services/composio/ComposioClient';
import { CONNECT_POPUP_WINDOW_NAME } from '../services/composio/connectPopupFlow';
import {
  completeFirstPartyReturn,
  firstPartyReturnOf,
  type FirstPartyReturn,
} from '../services/composio/firstPartyCallback';
import { reconcilePrest0nConnections } from '../services/prest0nSession/client';
import BrandWordmark from '../components/BrandWordmark';
import GlobalFooter from '../components/GlobalFooter';
import styled from 'styled-components';
import SeoHead from '../components/SeoHead';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
  min-height: calc(100vh - 80px);
`;

const PageHeader = styled.div`
  margin-bottom: 1.5rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.25rem;
`;

const Subtitle = styled.p`
  font-size: 1.05rem;
  color: #6B7280;
  line-height: 1.5;
`;

const PopupLandingWrap = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--brand-canvas);
  padding: 1.5rem;
`;

const PopupCard = styled.div`
  max-width: 380px;
  width: 100%;
  background: #fff;
  border: 1px solid var(--brand-line);
  border-radius: 16px;
  padding: 2rem 1.75rem;
  text-align: center;
`;

const PopupStatus = styled.p`
  font-size: 1.05rem;
  color: #111827;
  margin: 1rem 0 0.5rem;
  font-weight: 600;
`;

const PopupHint = styled.p`
  font-size: 0.92rem;
  color: #6b7280;
  line-height: 1.5;
  margin: 0;
`;

/**
 * Minimal landing for the OAuth popup. Everything that happens NEXT (folder
 * choice, green check) belongs to the window the user started from — its
 * 3-second connection poll picks the new connection up. `noopener` popups
 * cannot always close themselves, so the copy covers both outcomes.
 */
const ConnectPopupLanding: React.FC<{ appName: string | null; succeeded: boolean; surface?: string }> = ({
  appName,
  succeeded,
  surface,
}) => {
  // Some browsers (mobile tabs especially) refuse window.close() — after the
  // close attempts, offer an explicit way back to where the user started
  // instead of stranding them on this landing (2026-08-19).
  const [closeRefused, setCloseRefused] = useState(false);
  useEffect(() => {
    clearConnectPopupLaunch();
    if (succeeded) {
      // Tell the assistant right away (capability reconcile) — the opener's
      // poll is the backstop.
      void reconcilePrest0nConnections().catch(() => { /* best-effort */ });
    }
    // Two close attempts: script-opened windows may close immediately; the
    // 1200ms retry covers slow paints. A window that survives both gets the
    // manual continue button.
    try { window.close(); } catch { /* browser may refuse */ }
    const t = window.setTimeout(() => {
      try { window.close(); } catch { /* browser may refuse — copy covers it */ }
    }, 1200);
    const refused = window.setTimeout(() => setCloseRefused(true), 2000);
    return () => { window.clearTimeout(t); window.clearTimeout(refused); };
  }, [succeeded]);

  const app = appName || 'the app';
  const continueHref = surface === 'onboarding' ? '/onboarding' : '/integrations';
  return (
    <PopupLandingWrap>
      <PopupCard>
        <BrandWordmark href="#" />
        <PopupStatus>
          {succeeded ? `✓ ${app} connected` : `${app} didn't finish connecting`}
        </PopupStatus>
        <PopupHint>
          {succeeded
            ? 'all set — close this window and pick up where you left off. the window you started from updates on its own.'
            : 'no changes were made. close this window and press connect again when you\'re ready.'}
        </PopupHint>
        {closeRefused && (
          <PopupHint as="a" href={continueHref} style={{ color: '#7a5f1f', fontWeight: 600, textDecoration: 'underline' }}>
            {surface === 'onboarding' ? 'back to setup →' : 'back to integrations →'}
          </PopupHint>
        )}
      </PopupCard>
    </PopupLandingWrap>
  );
};

/**
 * An app Prest0n connects through its OWN OAuth client (Upwork, PR B1) came back here: the code leaves the address
 * bar at once, the server completes the connection, and the ordinary landing then says how it went.
 */
const FirstPartyReturnLanding: React.FC<{ ret: FirstPartyReturn; surface?: string }> = ({ ret, surface }) => {
  const [outcome, setOutcome] = useState<'pending' | 'ok' | 'failed'>('pending');
  useEffect(() => {
    try { window.history.replaceState(null, '', '/integrations/callback'); } catch { /* the post still runs */ }
    let live = true;
    void completeFirstPartyReturn(ret).then((ok) => { if (live) setOutcome(ok ? 'ok' : 'failed'); });
    return () => { live = false; };
  }, [ret]);
  if (outcome === 'pending') {
    return (
      <PopupLandingWrap>
        <PopupCard>
          <BrandWordmark href="#" />
          <PopupStatus>connecting Upwork…</PopupStatus>
        </PopupCard>
      </PopupLandingWrap>
    );
  }
  return <ConnectPopupLanding appName="Upwork" succeeded={outcome === 'ok'} surface={surface} />;
};

const Integrations: React.FC = () => {
  // An Upwork sign-in coming back (its state carries the server's prefix), decided once at mount.
  const [firstPartyReturn] = useState(() => firstPartyReturnOf(window.location.pathname, window.location.search));
  // Decide ONCE at mount whether this window is the OAuth popup: fresh
  // launch mark + we're on the callback path. State (not a bare computation)
  // so clearing the mark can't flip the render mid-session.
  const [popupInfo] = useState(() => {
    if (window.location.pathname !== '/integrations/callback') return null;
    const mark = readConnectPopupLaunch();
    // The window NAME is the second proof-of-popup (2026-08-19): it survives
    // the whole OAuth redirect chain and private-mode localStorage failures,
    // so the minimal landing (and its self-close) still renders when the
    // mark is missing or stale.
    const isNamedPopup = window.name === CONNECT_POPUP_WINDOW_NAME;
    if (!mark && !isNamedPopup) return null;
    const params = new URLSearchParams(window.location.search);
    return {
      appName: params.get('appName') || mark?.slug || null,
      succeeded: params.get('status') === 'success',
      surface: mark?.surface,
    };
  });

  if (firstPartyReturn) {
    return <FirstPartyReturnLanding ret={firstPartyReturn} surface={popupInfo?.surface} />;
  }
  if (popupInfo) {
    return <ConnectPopupLanding appName={popupInfo.appName} succeeded={popupInfo.succeeded} surface={popupInfo.surface} />;
  }

  return (
    <>
      <SeoHead
        title="Integrations — prest0"
        description="Connect prest0 with 1,000+ tools organizations already use — mail, calendars, storage, billing, and more."
        canonicalUrl="https://prest0.ai/integrations"
        keywords="enterprise software integrations, cloud integrations, enterprise productivity tools, SaaS integrations, workflow automation, connected enterprise stack"
      />
      <PageContainer>
        <PageHeader>
          <Title>Integrations</Title>
          <Subtitle>
            Connect the tools your organization uses every day. Your AI assistant works across email, calendars, documents, billing, and more — all from one place.
          </Subtitle>
        </PageHeader>
        <IntegrationsPanel />
      </PageContainer>
      <GlobalFooter />
    </>
  );
};

export default Integrations;
